import React from "react";
import { ActionIcon, Group, Input, MultiSelect, Tooltip } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconInfoCircle } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import FiscalPeriodSelect from "./FiscalPeriodSelect";
import { ACCOUNT_TYPE_OPTIONS } from "./trialBalanceUtils";

export const REPORT_FILTER_WIDTH = 220;

const FieldLabelWithTip = ({ label, tip }) => (
  <Group spacing={4} mb={4} noWrap>
    <Input.Label size="sm" mb={0}>
      {label}
    </Input.Label>
    <Tooltip label={tip} multiline width={280} withArrow>
      <ActionIcon size="xs" variant="transparent" color="gray" tabIndex={-1}>
        <IconInfoCircle size={14} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  </Group>
);

const AccountingReportFilters = ({
  fiscalPeriodId,
  onFiscalPeriodChange,
  asOfDate,
  onAsOfDateChange,
  showAsOfDate = false,
  pnlStartDate,
  onPnlStartDateChange,
  pnlEndDate,
  onPnlEndDateChange,
  showPnlDateRange = false,
  bsAsOfDate,
  onBsAsOfDateChange,
  showBsAsOfDate = false,
  accountTypes,
  onAccountTypesChange,
  showAccountTypeFilter = false,
  children,
}) => {
  const { t } = useTranslation();

  const accountTypeData = ACCOUNT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

  return (
    <Group mb="md" align="flex-end" spacing="sm">
      <FiscalPeriodSelect
        size="sm"
        label={t("Fiscal Period")}
        value={fiscalPeriodId}
        onChange={onFiscalPeriodChange}
        style={{ width: REPORT_FILTER_WIDTH }}
      />
      {showPnlDateRange && (
        <>
          <div style={{ width: REPORT_FILTER_WIDTH }}>
            <Input.Label size="sm" mb={4}>
              {t("accounting.financialStatements.pnlFrom")}
            </Input.Label>
            <DatePicker
              size="sm"
              placeholder={t("accounting.financialStatements.pnlFrom")}
              value={pnlStartDate}
              onChange={onPnlStartDateChange}
              allowFreeInput
              clearable
            />
          </div>
          <div style={{ width: REPORT_FILTER_WIDTH }}>
            <Input.Label size="sm" mb={4}>
              {t("accounting.financialStatements.pnlTo")}
            </Input.Label>
            <DatePicker
              size="sm"
              placeholder={t("accounting.financialStatements.pnlTo")}
              value={pnlEndDate}
              onChange={onPnlEndDateChange}
              allowFreeInput
              clearable
            />
          </div>
        </>
      )}
      {showBsAsOfDate && (
        <div style={{ width: REPORT_FILTER_WIDTH }}>
          <FieldLabelWithTip
            label={t("accounting.financialStatements.bsAsOf")}
            tip={t("accounting.financialStatements.bsAsOfTip")}
          />
          <DatePicker
            size="sm"
            placeholder={t("accounting.financialStatements.bsAsOf")}
            value={bsAsOfDate}
            onChange={onBsAsOfDateChange}
            allowFreeInput
            clearable
          />
        </div>
      )}
      {showAsOfDate && (
        <div style={{ width: REPORT_FILTER_WIDTH }}>
          <FieldLabelWithTip
            label={t("accounting.trialBalance.asOfDate")}
            tip={t("accounting.trialBalance.asOfDateTip")}
          />
          <DatePicker
            size="sm"
            placeholder={t("accounting.trialBalance.asOfDate")}
            value={asOfDate}
            onChange={onAsOfDateChange}
            allowFreeInput
            clearable
          />
        </div>
      )}
      {showAccountTypeFilter && (
        <MultiSelect
          size="sm"
          label={t("accounting.trialBalance.accountTypeFilter")}
          placeholder={t("accounting.trialBalance.accountTypeAll")}
          data={accountTypeData}
          value={accountTypes}
          onChange={onAccountTypesChange}
          clearable
          searchable
          style={{ width: REPORT_FILTER_WIDTH }}
        />
      )}
      {children}
    </Group>
  );
};

export default AccountingReportFilters;
